import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Milestone, MilestoneStage } from '@models';
import { filter, map, Observable, take } from 'rxjs';

import { UserDataService } from '../userdata/userdata.service';

@Injectable({
  providedIn: 'root'
})
export class CommonFunctionsService {
  private userDataService: UserDataService = inject(UserDataService);

  resizeChartWhenContainerIsReady(
    chart: { getDom: () => HTMLElement | null; resize: () => void },
    options?: {
      maxAttempts?: number;
      delayMs?: number;
      attempt?: number;
      shouldStop?: () => boolean;
    }
  ): void {
    const maxAttempts = options?.maxAttempts ?? 6;
    const delayMs = options?.delayMs ?? 60;
    const attempt = options?.attempt ?? 0;
    const shouldStop = options?.shouldStop;

    if (shouldStop?.()) {
      return;
    }

    const chartDom = chart.getDom();
    const hasValidSize = !!chartDom && chartDom.clientWidth > 0 && chartDom.clientHeight > 0;

    if (hasValidSize) {
      chart.resize();
      return;
    }

    if (attempt >= maxAttempts) {
      return;
    }

    setTimeout(
      () =>
        this.resizeChartWhenContainerIsReady(chart, {
          maxAttempts,
          delayMs,
          attempt: attempt + 1,
          shouldStop
        }),
      delayMs
    );
  }

  formatDate(date: string | null): string {
    if (!date) return '--';
    const safeDate = date?.replace(' ', 'T');
    const dateObj = new Date(safeDate);
    if (isNaN(dateObj.getTime())) return '--';

    return this.getFormattedDate(dateObj);
  }

  formatUndefinedDate(date: string | null): string | undefined {
    if (!date) return;
    const safeDate = date?.replace(' ', 'T');
    const dateObj = new Date(safeDate);

    if (isNaN(dateObj.getTime())) return;

    return this.getFormattedDate(dateObj);
  }

  formatNumberWithCommas(value: number): string {
    return value.toLocaleString();
  }

  roundValue(value: number, roundingDigits: number): number {
    return parseFloat(value.toFixed(roundingDigits));
  }

  getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  getMilestonesFlatMapFromStages(stages: MilestoneStage[]): Milestone[] {
    return stages.flatMap((stage) =>
      stage.milestones.flatMap((item) => (item.type === 'group' ? item.milestones : [item]))
    );
  }

  toSentenceCase(text: string | undefined): string {
    if (!text || text === '') return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  getRequestCommonParams(params: string[]): Observable<HttpParams> {
    return this.userDataService.isUserDataLoaded$.pipe(
      filter(Boolean),
      take(1),
      map(() => {
        let httpParams: HttpParams = new HttpParams();
        params.forEach((param) => {
          switch (param) {
            case 'user_id':
              httpParams = httpParams.set(param, this.userDataService.getUsername() ?? '');
              return;
            case 'org_ids':
              httpParams = httpParams.set(
                param,
                this.userDataService.getUserOrgIds()?.join(',') ?? ''
              );
              return;
            case 'parent_client_names':
              httpParams = httpParams.set(
                param,
                JSON.stringify(this.userDataService.getParentClientNames() ?? [])
              );
              return;
            case 'principal_id':
              httpParams = httpParams.set(param, this.userDataService.getPrincipalId() ?? '');
              return;
            default:
              return;
          }
        });
        return httpParams;
      })
    );
  }

  private getFormattedDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const monthAbbr = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day}-${monthAbbr}-${year}`;
  }
}
